import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaXTwitter, FaFacebookF, FaLinkedinIn, FaPinterestP } from 'react-icons/fa6';
import { Send, ArrowUp } from 'lucide-react';
import { motion } from 'framer-motion';
import ScrollAnimate, { fadeInUp, staggerContainer } from './ScrollAnimate';
import Input from './ui/Input';
import Button from './ui/Button';
import COLORS from '../utils/colors';

// Reusable Footer Column Component for animation
const FooterColumn = ({ title, children }) => (
  <ScrollAnimate variants={fadeInUp} tag="div">
    <h3 className="font-bold text-lg sm:text-xl mb-4 text-white" style={{ fontFamily: 'var(--font-primary)' }}>
      {title}
    </h3>
    <ul className="space-y-2 text-sm sm:text-base font-semibold text-white">
      {children}
    </ul>
  </ScrollAnimate>
);

// Reusable Footer Link Item
const FooterLink = ({ to, children, onClick }) => (
  <li>
    <motion.div whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
      <Link
        to={to}
        onClick={onClick}
        className="hover:text-[var(--color-accent)] hover:underline transition-all duration-300 inline-block"
      >
        {children}
      </Link>
    </motion.div>
  </li>
);

export default function VFooter() {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    // Mock subscription - replace with actual API call
    setTimeout(() => {
      setIsSubscribing(false);
      setEmail('');
      alert('Thank you for subscribing!');
    }, 1500);
  };

  const socialLinks = [
    { href: "https://www.instagram.com/vasstr.india/", icon: <FaInstagram />, label: "Instagram", color: "#E4405F" },
    { href: "https://x.com/vasstr_india", icon: <FaXTwitter />, label: "X/Twitter", color: "#000000" },
    { href: "https://www.facebook.com/vasstr.india", icon: <FaFacebookF />, label: "Facebook", color: "#1877F2" },
    { href: "https://www.linkedin.com/company/vasstr/", icon: <FaLinkedinIn />, label: "LinkedIn", color: "#0A66C2" },
    { href: "https://in.pinterest.com/vasstr_india/", icon: <FaPinterestP />, label: "Pinterest", color: "#E60023" },
  ];

  return (
    <footer className="text-white pt-12 pb-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: COLORS.MIDNIGHT, fontFamily: "var(--font-primary)" }}>
      <ScrollAnimate
        variants={staggerContainer(0.2)}
        className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 text-left mb-8"
        tag="div"
      >
        {/* Company Section */}
        <FooterColumn title="COMPANY">
          <FooterLink to="/" onClick={scrollToTop}>About Us</FooterLink>
          <FooterLink to="/contactus" onClick={scrollToTop}>Contact Us</FooterLink>
        </FooterColumn>

        {/* Policies Section */}
        <FooterColumn title="POLICIES">
          <FooterLink to="/privacyPolicy" onClick={scrollToTop}>Privacy Policy</FooterLink>
          <FooterLink to="/TermsNConditions" onClick={scrollToTop}>Terms of Service</FooterLink>
          <FooterLink to="/rtnPolicy" onClick={scrollToTop}>Exchange Policy</FooterLink>
        </FooterColumn>

        {/* Support Section */}
        <FooterColumn title="SUPPORT">
          <FooterLink to="/payShip" onClick={scrollToTop}>Shipping & Payments</FooterLink>
          <FooterLink to="/Faqs" onClick={scrollToTop}>FAQ's</FooterLink>
        </FooterColumn>

        {/* Newsletter & Social Section */}
        <ScrollAnimate variants={fadeInUp} tag="div">
          <h3 className="font-bold text-lg sm:text-xl mb-4 text-white">STAY CONNECTED</h3>

          {/* Newsletter Form */}
          <form onSubmit={handleNewsletterSubmit} className="mb-6">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-[var(--color-primary)] border-[var(--color-gray-600)] text-white placeholder:text-[var(--color-gray-400)]"
                rightIcon={<Send size={18} />}
              />
              <Button
                type="submit"
                variant="accent"
                size="md"
                loading={isSubscribing}
                className="sm:w-auto w-full"
              >
                Subscribe
              </Button>
            </div>
          </form>

          {/* Social Icons */}
          <div className="flex space-x-4 text-2xl">
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="text-white hover:text-[var(--color-accent)] transition-all duration-300"
                whileHover={{ scale: 1.2, y: -4 }}
                whileTap={{ scale: 0.9 }}
              >
                {social.icon}
              </motion.a>
            ))}
          </div>
        </ScrollAnimate>
      </ScrollAnimate>

      <ScrollAnimate variants={fadeInUp} tag="div">
        <hr className="my-6 md:my-8 border-[var(--color-gray-700)]" />
      </ScrollAnimate>

      <ScrollAnimate variants={fadeInUp} tag="div" className="text-center text-xs sm:text-sm font-semibold text-white">
        <p className="mb-4">
          © {new Date().getFullYear()} Vasstr. All rights reserved.
        </p>
        <motion.button
          onClick={scrollToTop}
          className="inline-flex items-center space-x-2 px-4 py-2 text-white hover:text-[var(--color-accent)] hover:bg-[var(--color-primary)] rounded-[var(--radius-md)] transition-all duration-300"
          whileHover={{ y: -4 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Back to top"
        >
          <span>Back to Top</span>
          <ArrowUp size={16} />
        </motion.button>
      </ScrollAnimate>
    </footer>
  );
}